import React, { useCallback, useState } from 'react';
import { Flex, Select } from 'antd';

import { Tag, TagType } from '../../Tag';

import cn from './styles.module.scss';

type TagSelectProps = {
  tags: TagType[],
  inputValue: string,
  onChange: (tags: string[]) => void,
  handleClose: (value: number) => void,
  loading?: boolean,
  handleSearch?: (value: string) => void,
  searchResult: TagType[],
  closeContainer?: () => void,
};

export const TagSelect = ({
  tags,
  inputValue,
  onChange,
  handleClose,
  loading,
  handleSearch,
  searchResult,
  closeContainer,
}: TagSelectProps) => {
  const [searchValue, setSearchValue] = useState('');

  const handleInputKeyDown = ({ key }: { key: string }) => {
    if (key === 'Enter') {
      if (!tags.find((tag: TagType) => tag.name.toLowerCase() === inputValue.toLowerCase())) {
        onChange([...tags.map((tag: TagType) => tag.name), inputValue]);
        if (closeContainer) {
          closeContainer();
        }
      }
    }
  };

  const handleSelect = (newTag: TagType) => {
    onChange([...tags.map((tag: TagType) => tag.name), newTag.name]);
    if (closeContainer) {
      closeContainer();
    }
  };

  const handleOnSearch = (value: string) => {
    setSearchValue(value);
    if (handleSearch) {
      handleSearch(value);
    }
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
  };

  const tagRenderer = useCallback(({ value }: any) => {
    const tag = tags.find((curTag: TagType) => curTag.id === value);

    if (!tag) {
      return null;
    }

    return (
      <Tag
        tag={tag}
        closable
        onClose={() => handleClose(value)}
        active
        maxLength={8}
        style={{ marginInlineEnd: 4 }}
      />
    );
  }, [tags, handleClose]);

  const dropdownRender = useCallback(() => {
    if (!loading && searchValue && searchResult.length === 0) {
      return <div className={cn.noContent}>{`Add tag "${searchValue}"`}</div>;
    }

    return (
      <Flex gap="small" align="center" wrap className={cn.dropdownContent}>
        {searchResult.map((tag: TagType) => (
          <Tag tag={tag} key={tag.id} maxLength={8} onClick={handleSelect} className={cn.tag}/>
        ))}
      </Flex>
    );
  }, [loading, searchValue]);

  return (
    <Select
      labelInValue
      mode="multiple"
      filterOption={false}
      tagRender={tagRenderer as any}
      value={tags.map((tag: TagType) => ({ label: tag.name, value: tag.id }))}
      placeholder="Select tags"
      loading={loading}
      onOpenChange={handleDropdownVisibleChange}
      showSearch={{
        onSearch: handleOnSearch
      }}
      className={cn.select}
      popupRender={dropdownRender}
      classNames={{
        popup: {
          root: cn.dropdown
        }
      }}
      options={searchResult as any}
      onInputKeyDown={handleInputKeyDown}
      data-testid="tagSelector"
    >
      {searchResult.map((tag: TagType) => (
        <Select.Option
          value={tag.name}
          key={tag.id}
          tag={tag}
        >
          {tag.name}
        </Select.Option>
      ))}
    </Select>
  );
};
